import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox, OutlineCard } from '@morressier/alembic';
import {
  FormFieldCheckboxArray as FormFieldCheckboxArrayType,
  ICustomFormFieldExternalOptions
} from '@publishing-engine/domain/types/article';
import { FieldLabel } from 'src/components/Field';
import { Flex, Text } from '@chakra-ui/react';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';

interface FormFieldCheckboxProps {
  config: FormFieldCheckboxArrayType;
  options?: ICustomFormFieldExternalOptions;
  isEditingAllowed?: boolean;
}

const FormFieldCheckboxArray: React.FC<FormFieldCheckboxProps> = ({
  config,
  options = [],
  isEditingAllowed = true
}) => {
  const { watch, setValue, clearErrors } = useFormContext();
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;
  // Always ensure value is an array
  const value = (watch(field) as string[]) || [];

  // Convert labels.options object → array with keys preserved
  if (options.length === 0) {
    options = Object.entries(labels.options || {}).map(([key, opt]) => ({
      id: key, // stable identifier (english_editing, figure_generation, etc.)
      main: opt.main,
      secondary: opt.secondary
    }));
  }

  const handleCheckboxChange = (
    checked: boolean,
    itemValue: string,
    currentValues: string[],
    field: string,
    setValue: Function,
    clearErrors: Function
  ) => {
    clearErrors(field);

    let newValue: string[] = [];

    if (checked) {
      if (itemValue === 'not_applicable') {
        newValue = ['not_applicable'];
      } else {
        newValue = [
          ...currentValues.filter(v => v !== 'not_applicable'),
          itemValue
        ];
      }
    } else {
      newValue = currentValues.filter(v => v !== itemValue);
    }

    setValue(field, newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  return (
    <Flex flexDirection="column" gap={2} width="100%">
      <OutlineCard borderless padding={8} width="100%" id={labels.main}>
        {!options.length ? (
          <Text>No {labels.subtitle || labels.main}</Text>
        ) : (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            gap={4}
            width="100%"
          >
            <FieldLabel
              isRequired={validations.required}
              label={labels.subtitle}
              subText={`${value.length}/${options.length} ${labels.main} Selected`}
              helpText={labels.helpText}
            />

            <Flex flexDirection="column" alignItems="flex-start" gap={4}>
              {options.map(item => (
                <Checkbox
                  key={item.id}
                  ariaLabel={item.main}
                  id={`${field}-${item.id}`}
                  name={field}
                  isChecked={value.includes(item.id)}
                  isDisabled={!isEditingAllowed}
                  onChange={({ target: { checked } }) =>
                    handleCheckboxChange(
                      checked,
                      item.id,
                      value,
                      field,
                      setValue,
                      clearErrors
                    )
                  }
                >
                  <Flex flexDirection="column">
                    <Text textStyle={'textBody'}>{item.main}</Text>
                    {item.secondary && (
                      <Text textStyle={'textBody'}>{item.secondary}</Text>
                    )}
                  </Flex>
                </Checkbox>
              ))}
            </Flex>
          </Flex>
        )}
      </OutlineCard>

      <FormFieldErrorMessage config={config} />
    </Flex>
  );
};

export default FormFieldCheckboxArray;
